import _ from 'underscore';
import Augment from './_Augment';

var METADATA_CLASS = 'edge.aspects.validation.dto.ValidationRuleDTO';

/** Validitiy descriptor for any type. Considers value as always valid. */
var ANY_TYPE = {
    'typeValid': _.constant(true),
    'messages': _.constant([])
};

var INT_TYPES = ['byte', 'short', 'int', 'long', 'Byte', 'Short', 'Integer', 'Long', 'BigInteger'];
var FLOAT_TYPES = ['float', 'double', 'Float', 'Double', 'BigDecimal'];

var alwaysTrue = _.constant(true);
var alwaysEmpty = _.constant([]);

/**
 * Creates a new "Field is required" aspect.
 * @param {Object} expressionLanguage
 * @param {Object} validationMessages
 * @returns  {Object}
 */
function create(expressionLanguage, validationMessages) {
    /** Validity for the decimal type. */
    var DECIMAL_TYPE = {
        'typeValid': function typeValid(val) {
            if (_.isNumber(val)) {
                return true;
            }
            return (/^-?\d*(\.\d*)?$/.test(val)
            );
        },
        'messages': function messages() /* val*/{
            return [validationMessages.notANumber];
        }
    };

    /** Validity descriptor for the integer types. */
    var INT_TYPE = {
        'typeValid': function typeValid(val) {
            if (_.isNumber(val)) {
                return Math.round(val) === val;
            }

            return (/^-?\d+$/.test(val)
            );
        },
        'messages': function messages(val) {
            if (!DECIMAL_TYPE.typeValid(val)) {
                return DECIMAL_TYPE.messages(val);
            }
            return [validationMessages.notAnInteger];
        }
    };

    function getAllRules(compilationContext, node, nodeMetadata, ancestorChain) {
        return Augment.collectRules(compilationContext, node, nodeMetadata, ancestorChain, METADATA_CLASS);
    }

    function compileRule(compilationContext, rule) {
        var validationRule = compilationContext.compile(rule.data.expression);
        var messageExpr = compilationContext.compile(rule.data.message);

        return {
            'shouldApply': rule.shouldApply,
            'validate': validationRule,
            'message': messageExpr
        };
    }

    function combineRules(rules) {
        function getMessages(v, pv, ctx) {
            return rules.filter(function (rule) {
                /* `rule.validate` returns `true`, `false` or `NONSENSE`.
                 NONSENSE handling: shouldApply do that internally. Negation is fine for it. */
                return rule.shouldApply() && !rule.validate(v, pv, ctx);
            }).map(function (rule) {
                return rule.message(v, pv, ctx);
            });
        }

        return {
            'messages': getMessages,
            'valid': function valid(v, pv, ctx) {
                var _iteratorNormalCompletion = true;
                var _didIteratorError = false;
                var _iteratorError = undefined;

                try {
                    for (var _iterator = rules[Symbol.iterator](), _step; !(_iteratorNormalCompletion = (_step = _iterator.next()).done); _iteratorNormalCompletion = true) {
                        var rule = _step.value;

                        /* `rule.validate` returns `true`, `false` or `NONSENSE`.
                         NONSENSE handling: shouldApply internally. Explicit check in validate. */
                        if (rule.shouldApply() && !(rule.validate(v, pv, ctx) === true)) {
                            return false;
                        }
                    }
                } catch (err) {
                    _didIteratorError = true;
                    _iteratorError = err;
                } finally {
                    try {
                        if (!_iteratorNormalCompletion && _iterator.return) {
                            _iterator.return();
                        }
                    } finally {
                        if (_didIteratorError) {
                            throw _iteratorError;
                        }
                    }
                }

                return true;
            }
        };
    }

    /**
     * Returns a type validation descriptor based on the DTO type.
     * @param {Object} type
     * @returns {Object}
     */
    function getTypeDescriptor(type) {
        switch (type.kind) {
            case 'primitive':
                if (INT_TYPES.indexOf(type.name) >= 0) {
                    return INT_TYPE;
                }
                if (FLOAT_TYPES.indexOf(type.name) >= 0) {
                    return DECIMAL_TYPE;
                }
                return ANY_TYPE;

            default:
                return ANY_TYPE;
        }
    }

    function valueNotSet(currentViewModelNode) {
        var val = currentViewModelNode.value;
        return _.isUndefined(val) || _.isNull(val) || val === '';
    }

    return {
        'getAspectProperties': function getAspectProperties(currentViewModelNode, currentMetadataNode, ancestorChain) {
            // `var` because otherwise causes deoptimization: 'Unsupported phi use of const or let variable'
            var readOnlyProperty = currentMetadataNode.readOnly; // eslint-disable-line no-var

            var compilationContext = expressionLanguage.getCompilationContext(currentMetadataNode.xCenter);
            var rules = getAllRules(compilationContext, currentViewModelNode, currentMetadataNode, ancestorChain);
            var compiledRules = new Array(rules.length);
            for (var i = 0; i < rules.length; i++) {
                compiledRules[i] = compileRule(compilationContext, rules[i]);
            }

            var descriptor = combineRules(compiledRules);
            var typeDescriptor = getTypeDescriptor(currentMetadataNode.valueType);

            /* Angular evaluation model workaround. */
            var lastMessages = null;

            var allMessages = readOnlyProperty ? alwaysEmpty : function allMessages() {
                var allMsgs = void 0;
                if (valueNotSet(currentViewModelNode)) {
                    allMsgs = currentViewModelNode.aspects._requirednessMessages;
                } else if (!typeDescriptor.typeValid(currentViewModelNode.value)) {
                    allMsgs = typeDescriptor.messages(currentViewModelNode.value);
                } else {
                    allMsgs = descriptor.messages(currentViewModelNode, ancestorChain.parent, currentViewModelNode.aspects.context);
                }

                var newMessages = _.unique(allMsgs);
                if (!_.isEqual(lastMessages, newMessages)) {
                    lastMessages = newMessages;
                }
                return lastMessages;
            };

            return {
                'valid': {
                    'get': readOnlyProperty ? alwaysTrue : function () {
                        if (valueNotSet(currentViewModelNode)) {
                            return !currentViewModelNode.aspects.required;
                        }

                        return typeDescriptor.typeValid(currentViewModelNode.value) && descriptor.valid(currentViewModelNode, ancestorChain.parent, currentViewModelNode.aspects.context);
                    }
                },
                'validationMessages': {
                    'get': allMessages
                },
                'validationMessage': {
                    'get': function get() {
                        return allMessages()[0];
                    }
                }
            };
        }
    };
}

export default {
    create: create
};


//////////////////
// WEBPACK FOOTER
// ./common/js/src/edge/api/gw-portals-viewmodel-js/aspects/Validity.js
// module id = 944
// module chunks = 0